import { z } from 'zod';
import { openWorldEntrySideSchema } from './entry';
import { OPEN_WORLD_MAX_EXPEDITION_MEMBERS } from './expedition';
import { openWorldCoordinateSchema } from './grid';

/**
 * **A PT do mundo aberto: um lobby mutável, e uma expedição congelada**
 * (gate V28I.Q).
 *
 * ## Duas coisas, e nunca uma
 *
 * O **lobby** é social e muda: alguém entra, alguém sai, o líder troca o lado,
 * uma prontidão levanta-se e cai. A **expedição** é uma fotografia dele, tirada
 * no instante do compromisso, e não muda mais — porque "quem é meu aliado"
 * decide se um tiro aterra, e uma relação que mudasse a meio de um combate
 * seria um projétil legal quando saiu e ilegal quando chegou.
 *
 * Misturá-las é o defeito que este ficheiro existe para impedir. Um convite
 * aceite não muda uma equipa a voar; sair da PT não devolve fogo amigo a quem
 * está no meio de uma expedição; e uma vaga de alguém que morreu não se
 * preenche.
 *
 * ## O que o navegador nunca diz
 *
 * Quem ele é, que é líder, quem é integrante, quem está pronto, qual é a
 * composição, qual é o digest, onde está, quanto vale ou o que carrega. Nenhuma
 * dessas coisas tem campo em nenhum dos pedidos abaixo — e é essa ausência, e
 * não uma verificação, que as torna impossíveis.
 *
 * A conta de quem pede vem sempre da sessão.
 */

/**
 * A versão da forma de um lobby e de uma expedição, no fio.
 *
 * **2** desde o gate V28I.Q.1: um integrante passou a dizer em que quadrante
 * está, se esse quadrante é um porto seguro, em que estado se encontra e sob
 * que época da localização isso foi lido. Nada saiu, e nada mudou de
 * significado — mas quem lê a vista tem de saber que ela diz mais.
 *
 * **3** desde o gate V28I.AA: o teto passou de nove para **sete**. A FORMA da
 * vista não mudou — nenhum campo entrou, saiu ou mudou de significado — e é por
 * isso que esta é a única versão que sobe: o que mudou foram as REGRAS do lobby,
 * e uma PT que ontem cabia deixou hoje de caber. Um cliente que continuasse a
 * desenhar "0/9" e a deixar convidar o oitavo estaria a prometer uma coisa que a
 * autoridade recusa, e é essa promessa que uma versão existe para apanhar.
 *
 * **4** desde o gate V28I.AD: uma recusa nova, `quadrant_full`. O quadrante
 * passou a ter lotação e uma expedição passa a poder ser recusada por não
 * caberem lá sete vagas — uma razão que nenhum cliente anterior sabe explicar.
 */
export const OPEN_WORLD_PARTY_VERSION = 4;

/**
 * **De quanto em quanto tempo o HUD volta a perguntar.** Provisório.
 *
 * Cinco segundos, e propositadamente lento. Uma PT não é um jogo de reflexos:
 * o que esta lista responde é "para onde vou para o encontrar", e essa
 * pergunta não muda entre dois quadros. Um intervalo curto seria uma sondagem
 * por movimento com outro nome.
 *
 * Ele só corre quando há alguma coisa para convergir — uma PT com mais do que
 * um integrante, ou uma expedição a decorrer — e para com a aba escondida.
 *
 * **Registado em `docs/OPEN_QUESTIONS.md` para medição final.**
 */
export const OPEN_WORLD_PARTY_POLL_MS = 5_000;

/**
 * **O teto: SETE, e é o mesmo número da composição** (gate V28I.AA).
 *
 * Lido de `OPEN_WORLD_MAX_EXPEDITION_MEMBERS` e nunca copiado: uma segunda
 * constante seria um lobby a aceitar oito pessoas para uma composição que só
 * cabe sete, e a recusa aconteceria no pior sítio possível — depois de toda a
 * gente ter dito que estava pronta.
 *
 * **Decidido pelo dono**, e já não provisório: eram nove, e nove estava
 * declarado provisório desde o dia em que foi escrito (ADR 0077, superada pela
 * ADR 0088). Sete é o número, e é sete em todo o lado — nada no jogo o reduz em
 * silêncio.
 *
 * **Não é a lotação de um quadrante.** Essa continua por medir e continua aberta
 * em `docs/OPEN_QUESTIONS.md`: um quadrante pode conter várias PTs, pilotos
 * sozinhos e inimigos ao mesmo tempo.
 */
export const OPEN_WORLD_MAX_PARTY_MEMBERS = OPEN_WORLD_MAX_EXPEDITION_MEMBERS;

/**
 * **Quanto tempo um convite vive.** Dez minutos.
 *
 * Conservador de propósito, e **provisório**: um convite é uma promessa de
 * atenção, não uma reserva de vaga, e um que durasse horas encheria a caixa de
 * alguém com convites de grupos que já partiram. Dez minutos é tempo de ir
 * buscar um copo de água e voltar.
 *
 * Centralizado num sítio: quem cria, quem lê e quem varre usam este número, e
 * dois prazos seriam duas respostas para "isto ainda vale".
 */
export const OPEN_WORLD_PARTY_INVITE_TTL_MS = 10 * 60_000;

/**
 * **Quantos convites uma conta pode mandar por minuto.** Seis.
 *
 * Também provisório. Ele existe porque uma rota que resolve um nome é uma rota
 * que alguém pode martelar, e o limite vive na infraestrutura de limitação que
 * já existe — nunca num contador em memória que desaparece ao reiniciar.
 */
export const OPEN_WORLD_PARTY_INVITE_RATE_PER_MINUTE = 6;

/* -------------------------------------------------------------------------- */
/*  As rotas                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Uma rota de leitura e uma de ação, e não nove.
 *
 * A ação leva o que quer fazer num campo fechado, porque as nove partilham
 * exatamente a mesma resposta: **o lobby inteiro, como quem pediu o vê**. Nove
 * rotas seriam nove sítios a montar a mesma vista, e a décima esqueceria um
 * campo.
 */
export const OPEN_WORLD_PARTY_STATE_ROUTE = '/api/open-world/party';
export const OPEN_WORLD_PARTY_ACTION_ROUTE = '/api/open-world/party/action';

/* -------------------------------------------------------------------------- */
/*  O que se pode pedir                                                       */
/* -------------------------------------------------------------------------- */

/** O nome público de uma conta, como ele viaja. Nunca um email, nunca um id. */
export const openWorldPartyNameSchema = z.string().min(1).max(64);

/**
 * **As nove ações.**
 *
 * Nenhuma delas nomeia quem a pede: isso vem da sessão. Só três nomeiam outra
 * pessoa — convidar, cancelar e expulsar — e as duas últimas fazem-no pelo id
 * público que o próprio lobby já mostrou a quem está nele, e não por uma
 * pesquisa.
 */
export const openWorldPartyActionSchema = z.discriminatedUnion('do', [
  /** **Criar.** Quem cria é o líder, e não há campo para dizer o contrário. */
  z.object({ do: z.literal('create') }).strict(),
  /**
   * **Convidar**, pelo nome público EXATO.
   *
   * Exato e nunca parcial: uma rota que aceitasse um prefixo seria uma
   * ferramenta de descoberta de contas. Uma falha responde sempre a mesma
   * coisa, seja o nome inexistente, ambíguo, o próprio, já convidado, já noutra
   * PT ou a PT cheia — a rota não distingue nenhum desses casos para quem
   * pergunta.
   */
  z.object({ do: z.literal('invite'), name: openWorldPartyNameSchema }).strict(),
  /** **Aceitar** um convite recebido, pela identidade dele. */
  z.object({ do: z.literal('accept'), inviteId: z.uuid() }).strict(),
  /** **Recusar** um convite recebido. */
  z.object({ do: z.literal('decline'), inviteId: z.uuid() }).strict(),
  /** **Cancelar** um convite que esta PT mandou. Só o líder. */
  z.object({ do: z.literal('cancel'), inviteId: z.uuid() }).strict(),
  /** **Sair.** Vale para o líder e para qualquer integrante. */
  z.object({ do: z.literal('leave') }).strict(),
  /** **Expulsar** um integrante, antes da expedição. Só o líder. */
  z.object({ do: z.literal('kick'), memberId: z.uuid() }).strict(),
  /** **Pronto**, ou já não. Cada um só fala por si. */
  z.object({ do: z.literal('ready'), ready: z.boolean() }).strict(),
  /** **O lado de entrada.** Só o líder, e nunca uma coordenada. */
  z.object({ do: z.literal('side'), side: openWorldEntrySideSchema }).strict(),
  /**
   * **Iniciar a expedição.** Só o líder.
   *
   * Leva uma identidade de ordem: um clique repetido — ou um cliente honesto
   * que não viu a resposta — tem de LER o resultado em vez de o produzir outra
   * vez. Sem ela, dois cliques seriam duas expedições.
   */
  z.object({ do: z.literal('start'), commandId: z.uuid() }).strict(),
]);

export type OpenWorldPartyAction = z.infer<typeof openWorldPartyActionSchema>;

export const openWorldPartyRequestSchema = z
  .object({
    v: z.literal(OPEN_WORLD_PARTY_VERSION),
    action: openWorldPartyActionSchema,
  })
  .strict();

export type OpenWorldPartyRequest = z.infer<typeof openWorldPartyRequestSchema>;

/* -------------------------------------------------------------------------- */
/*  O que o servidor responde                                                 */
/* -------------------------------------------------------------------------- */

/**
 * **Porque não.** Uma lista fechada, e propositadamente pouco informativa
 * numa delas.
 *
 * `invite_failed` cobre tudo o que pode correr mal num convite, e cobre de
 * propósito: distinguir "não existe" de "já está noutra PT" transformaria a
 * rota numa maneira de descobrir quem existe e onde está.
 */
export const OPEN_WORLD_PARTY_REFUSALS = [
  /** O mundo aberto não está ligado nesta instalação. */
  'unavailable',
  /** Já está numa PT, e uma conta está numa. */
  'already_in_party',
  /** Não está em PT nenhuma, e a ação exigia uma. */
  'not_in_party',
  /** A ação é do líder, e quem pediu não é. */
  'not_leader',
  /** A PT está cheia. */
  'party_full',
  /** O convite não existe, expirou, ou não é para quem o tentou usar. */
  'invite_expired',
  /** Qualquer coisa que impeça um convite. Deliberadamente indistinta. */
  'invite_failed',
  /** A expedição desta PT está a decorrer: o lobby está congelado. */
  'expedition_active',
  /** Nem toda a gente está pronta, ou alguém deixou de poder entrar. */
  'not_ready',
  /** Alguém do grupo já está no mundo, num duelo, ou tem um recibo por ler. */
  'member_busy',
  /** A formação não cabe no porto escolhido. */
  'formation_full',
  /**
   * **O quadrante não tem vagas para a PT inteira** (gate V28I.AD).
   *
   * Sete integrantes precisam de sete vagas: ou ficam todos reservados, ou
   * nenhum fica. Metade de uma PT a entrar seria a PT partida pela porta.
   *
   * É diferente de `party_full`, que é o teto de sete da própria composição, e
   * de `formation_full`, que é a geometria do porto. As três regras são
   * independentes e nenhuma deriva de outra.
   */
  'quadrant_full',
  /** Demasiados pedidos. */
  'rate_limited',
] as const;

export type OpenWorldPartyRefusal = (typeof OPEN_WORLD_PARTY_REFUSALS)[number];

/** O que impede o líder de iniciar. Uma frase por integrante, e nunca um código. */
export const OPEN_WORLD_PARTY_BLOCKERS = [
  /** Ainda não marcou pronto. */
  'not_ready',
  /** Está no mundo aberto agora. */
  'in_world',
  /** Está num duelo. */
  'in_duel',
  /** Tem um recibo de extração por ler. */
  'receipt_pending',
  /** A caça dele não fechou em segurança. */
  'hunt_busy',
] as const;

export type OpenWorldPartyBlocker = (typeof OPEN_WORLD_PARTY_BLOCKERS)[number];

/**
 * **Onde cada integrante está, em oito palavras fechadas** (gate V28I.Q.1).
 *
 * Uma lista fechada e não uma frase: a interface tem de escrever cada uma, e um
 * nono estado inventado no servidor não compila do outro lado.
 *
 * Cada um sai de uma autoridade e de mais nenhuma, e nenhum é uma projeção do
 * navegador:
 *
 * | estado | de onde vem |
 * | --- | --- |
 * | `no_lobby` | não há expedição a decorrer |
 * | `entering` | há expedição, e o coordenador ainda não a nomeia |
 * | `active` | o coordenador tem localização viva, e a sala diz que há alguém aos comandos |
 * | `crossing` | o coordenador tem uma travessia no ar |
 * | `disconnected` | localização viva, e a sala diz que ninguém está aos comandos |
 * | `dead` | a extração desta expedição foi selada com `died` |
 * | `extracted` | selada com `extracted` |
 * | `left` | está na composição congelada e já não tem linha ativa no lobby |
 *
 * **`dead` e `extracted` ganham a tudo o resto.** Uma conta que morreu e voltou
 * a entrar noutra expedição não é dita morta — a leitura é por EXPEDIÇÃO, e não
 * por conta, e a expedição de onde ela saiu já não é esta.
 */
export const OPEN_WORLD_PARTY_MEMBER_STATES = [
  'no_lobby',
  'entering',
  'active',
  'crossing',
  'disconnected',
  'dead',
  'extracted',
  'left',
] as const;

export type OpenWorldPartyMemberState = (typeof OPEN_WORLD_PARTY_MEMBER_STATES)[number];
/**
 * **Um integrante, como o lobby o mostra.**
 *
 * O nome, se é o líder, se está pronto, e o que o impede. **Nunca** o email, o
 * inventário, a vida, a capacidade, a localização exata nem qualquer
 * identificador interno: o `id` é o id público da conta, que é o mesmo que já
 * viaja em todas as outras superfícies deste jogo.
 */
export const openWorldPartyMemberSchema = z
  .object({
    id: z.uuid(),
    name: openWorldPartyNameSchema,
    leader: z.boolean(),
    ready: z.boolean(),
    /** O que o impede de iniciar, ou nada. Visto por toda a PT, e por mais ninguém. */
    blocker: z.enum(OPEN_WORLD_PARTY_BLOCKERS).nullable(),
    /** Se ele é quem está a ler esta vista. Para a interface não ter de comparar ids. */
    self: z.boolean(),
    /**
     * **Em que estado ele está.** Uma das oito, e nunca uma frase.
     */
    state: z.enum(OPEN_WORLD_PARTY_MEMBER_STATES),
    /**
     * **Em que quadrante ele está**, ou nada quando não está no mundo.
     *
     * Um par de índices, que a interface escreve como `A/3`. **Nunca uma
     * posição dentro do quadrante**: um rótulo diz para onde ir, e uma
     * coordenada dizia onde disparar. Durante uma travessia é a ORIGEM até ao
     * ponto de compromisso, porque anunciar o destino antes disso seria
     * anunciar uma viagem que ainda pode ser recusada.
     */
    quadrant: openWorldCoordinateSchema.nullable(),
    /**
     * Se esse quadrante é um porto seguro. Nulo quando não há quadrante.
     *
     * Sai de `isOpenWorldSafePort` e de mais lado nenhum: a segurança de um
     * quadrante é uma propriedade da grelha, e uma segunda tabela a dizê-lo
     * seria uma segunda resposta que mais cedo ou mais tarde discordava.
     */
    safe: z.boolean().nullable(),
    /**
     * **A época da localização** sob a qual isto foi lido.
     *
     * Monótona por conta, e é ela que faz uma resposta atrasada ser
     * descartável em vez de confusa: uma vista composta antes de uma travessia
     * traz uma época menor, e quem a receber depois da nova não a aplica.
     */
    epoch: z.number().int().min(0),
  })
  .strict();

export type OpenWorldPartyMember = z.infer<typeof openWorldPartyMemberSchema>;

/** Um convite, como quem o mandou e quem o recebeu o veem. */
export const openWorldPartyInviteSchema = z
  .object({
    id: z.uuid(),
    /** O nome de quem convidou, para o destinatário saber quem é. */
    fromName: openWorldPartyNameSchema,
    /** E o de quem foi convidado, para o líder poder cancelá-lo. */
    toName: openWorldPartyNameSchema,
    /** Milissegundos até expirar. Zero quando já expirou. */
    ttlMs: z.number().int().min(0),
  })
  .strict();

export type OpenWorldPartyInvite = z.infer<typeof openWorldPartyInviteSchema>;

/**
 * **O lobby inteiro, como quem pediu o vê.**
 *
 * Uma vista e não um registo: ela é montada por conta, e o que ela mostra
 * depende de quem pergunta. Quem não está em PT nenhuma recebe `party: null` e
 * os convites que recebeu; quem está recebe a lista, o lado e a prontidão.
 */
export const openWorldPartyViewSchema = z
  .object({
    v: z.literal(OPEN_WORLD_PARTY_VERSION),
    /** A PT desta conta, ou nada. */
    party: z
      .object({
        id: z.uuid(),
        /** Os integrantes, pela ordem canónica de entrada. Um a nove. */
        members: z.array(openWorldPartyMemberSchema).min(1).max(OPEN_WORLD_MAX_PARTY_MEMBERS),
        /** O lado que o líder escolheu. */
        side: openWorldEntrySideSchema,
        /** Se quem lê é o líder. */
        leader: z.boolean(),
        /** Os convites que esta PT tem por responder. Só o líder os vê. */
        sent: z.array(openWorldPartyInviteSchema),
        /**
         * **A expedição desta PT está a decorrer.**
         *
         * Enquanto for verdade, o lobby está congelado: ninguém entra, ninguém
         * sai, e a prontidão não se mexe.
         */
        expeditionActive: z.boolean(),
        /**
         * O que impede o início, em palavras que a interface mostra.
         *
         * Vazio quer dizer que o líder pode iniciar. Ele é derivado da lista de
         * integrantes e recalculado a cada leitura — nunca guardado, porque um
         * bloqueio guardado envelhece.
         */
        blocked: z.array(z.enum(OPEN_WORLD_PARTY_BLOCKERS)),
      })
      .strict()
      .nullable(),
    /** Os convites que ESTA conta recebeu e ainda não respondeu. */
    received: z.array(openWorldPartyInviteSchema),
  })
  .strict();

export type OpenWorldPartyView = z.infer<typeof openWorldPartyViewSchema>;

/**
 * A resposta de uma ação: a vista nova, ou a razão de nada ter mudado.
 *
 * Sempre a vista inteira e nunca um diff: um lobby é pequeno, muda por
 * completo quando muda, e um cliente que aplicasse diferenças teria de as saber
 * ordenar — que é uma segunda máquina de estados a viver no navegador.
 */
export const openWorldPartyResponseSchema = z
  .object({
    ok: z.boolean(),
    refusal: z.enum(OPEN_WORLD_PARTY_REFUSALS).nullable(),
    view: openWorldPartyViewSchema,
    /**
     * **O bilhete**, quando a ação foi um início que se comprometeu.
     *
     * Ele nomeia o porto onde este piloto nasceu, e é a mesma capacidade de uso
     * único que uma entrada solo assina. Ausente em tudo o resto.
     */
    ticket: z.string().min(1).optional(),
  })
  .strict();

export type OpenWorldPartyResponse = z.infer<typeof openWorldPartyResponseSchema>;
